import { Box, Button, Dialog, Flex, Text, TextField, IconButton } from '@radix-ui/themes';
import { MdOutlineEdit } from 'react-icons/md';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '../../config/axiosConfig';
import { useFormik } from 'formik';
import { gatewaySchema } from '../../Schema';

const styles = `
  .input-error .rt-TextFieldInput {
    border-color: red;
  }
`;

const CreateGateway = ({ initialValues = null, onGatewayCreated, showToast }) => {
  const isEditMode = !!initialValues;
  const [isOpen, setIsOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      gatewayAddress: initialValues?.gateway_address || '',
      igin: initialValues?.igin || '',
      igvn: initialValues?.igvn || '',
    },
    validationSchema: gatewaySchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      try {
        const gatewayData = {
          token: isEditMode ? initialValues.token : null,
          gatewayAddress: values.gatewayAddress,
          igin: values.igin,
          igvn: values.igvn,
          roomToken: null,
          institutionToken: null,
          timeOut: 0,
          isActive: true,
          isGatewayOwned: false,
          isRegistered: false,
          isSuperAdmin: true,
          certificateThumbprint: null,
          certificateExpiration: null,
        };

        const endpoint = isEditMode ? '/gateway/update-gateway' : '/gateway/add-gateway';
        const response = await axiosInstance.post(endpoint, gatewayData);
        if (response.data.result) {
          const successMessage = isEditMode ? 'Gateway updated successfully' : 'Gateway created successfully';
          console.log(successMessage);
          if (showToast) showToast(successMessage);
          if (onGatewayCreated) await onGatewayCreated();
          setIsOpen(false);
          resetForm();
        } else {
          const errorMessage = response.data.errorResponse.message;
          console.error(errorMessage, response.data);
          if (showToast) showToast(errorMessage);
        }
      } catch (error) {
        const errorMessage = `Error ${isEditMode ? 'updating' : 'creating'} gateway`;
        console.error(errorMessage, error);
        if (showToast) showToast(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, isSubmitting, handleChange, handleBlur, handleSubmit, resetForm, setValues } = formik;

  // Update form values for edit mode
  useEffect(() => {
    if (isEditMode && isOpen && initialValues) {
      setValues({
        gatewayAddress: initialValues.gateway_address || '',
        igin: initialValues.igin || '',
        igvn: initialValues.igvn || '',
      });
    }
  }, [isOpen, isEditMode, initialValues, setValues]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const submitForm = (e) => {
    e.preventDefault();
    formik.validateForm().then(errors => {
      if (Object.keys(errors).length === 0) {
        formik.handleSubmit();
      } else {
        Object.keys(formik.values).forEach(field => {
          formik.setFieldTouched(field, true);
        });
      }
    });
  };

  const dialogContent = isOpen ? (
    <Dialog.Content maxWidth="450px">
      <Dialog.Title>{isEditMode ? 'Manage Gateway Details' : 'Add Gateway'}</Dialog.Title>
      <Dialog.Description size="2" mb="4">
        {isEditMode ? 'Modify the gateway’s details.' : 'Fill in the details to add a new gateway.'}
      </Dialog.Description>

      <form onSubmit={handleSubmit}>
        <style>{styles}</style>
        <Flex direction="column" gap="3">
          <label className="w-[100%]">
            <Text as="div" size="2" mb="1" weight="bold">Gateway Address</Text>
            <TextField.Root
              name="gatewayAddress"
              value={values.gatewayAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter gateway address"
              disabled={isEditMode}
              className={errors.gatewayAddress && touched.gatewayAddress ? 'input-error' : ''}
            />
            {errors.gatewayAddress && touched.gatewayAddress && (
              <Text size="1" color="red" mt="1">{errors.gatewayAddress}</Text>
            )}
          </label>
          <label className="w-[100%]">
            <Text as="div" size="2" mb="1" weight="bold">IGIN (Imacs Gateway Identification Number)</Text>
            <TextField.Root
              name="igin"
              value={values.igin}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter IGIN"
              className={errors.igin && touched.igin ? 'input-error' : ''}
            />
            {errors.igin && touched.igin && (
              <Text size="1" color="red" mt="1">{errors.igin}</Text>
            )}
          </label>
          <label className="w-[100%]">
            <Text as="div" size="2" mb="1" weight="bold">IGVN (Imacs Gateway Verification Number)</Text>
            <TextField.Root
              name="igvn"
              value={values.igvn}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter IGVN"
              className={errors.igvn && touched.igvn ? 'input-error' : ''}
            />
            {errors.igvn && touched.igvn && (
              <Text size="1" color="red" mt="1">{errors.igvn}</Text>
            )}
          </label>
        </Flex>
        <Flex gap="3" mt="4" justify="end">
          <Button
            variant="soft"
            color="gray"
            onClick={() => setIsOpen(false)}
            disabled={isSubmitting}
            type="button"
          >
            Cancel
          </Button>
          <Button type="button" disabled={isSubmitting} onClick={submitForm}>
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </Flex>
      </form>
    </Dialog.Content>
  ) : null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      {isEditMode ? (
        <Dialog.Trigger>
          <IconButton variant="outline" onClick={() => setIsOpen(true)}>
            <MdOutlineEdit width="18" height="18" />
          </IconButton>
        </Dialog.Trigger>
      ) : (
        <Dialog.Trigger>
          <Button onClick={() => setIsOpen(true)}>Add Gateway</Button>
        </Dialog.Trigger>
      )}
      {isOpen && createPortal(dialogContent, document.body)}
    </Dialog.Root>
  );
};

export default CreateGateway;